import type PrintPriceCalculator from '@shared/utils/print-price/PrintPriceCalculator';
import type { PriceSummaryData, PriceSummaryLineItem } from './PriceSummary.types';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import type { StationeryDiscountInfo, StationeryPriceInfoRawLineItem, StationeryProduct } from '@shared/utils/print-price/types';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer';
import { useGetCardDeliveryInfo } from './utils/useGetCardDeliveryInfo';
import type { CardPromotion } from './PriceSummary.controller';
import { usePriceCalculatorLineItemsToPriceSummaryLineItems } from './utils/usePriceCalculatorLineItemsToPriceSummaryLineItems';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { PriceSummaryCurrencyFormatOptions } from './PriceSummary.constants';

export { PriceSummaryCurrencyFormatOptions } from './PriceSummary.constants';

type LineItemStep = {
  stepAlias: string;
  targetSteps: string[];
  shouldIncludeQuantity?: boolean;
};

type ValidLineItems = {
  card: PriceSummaryLineItem[];
  back: PriceSummaryLineItem[];
  shipping: PriceSummaryLineItem[];
  tax: PriceSummaryLineItem[];
  envelope: PriceSummaryLineItem[];
};

const CARD_LINE_ITEM_STEPS: LineItemStep[] = [
  {
    stepAlias: 'card',
    targetSteps: ['cardFront'],
    shouldIncludeQuantity: true,
  },
  {
    stepAlias: 'back',
    targetSteps: ['cardBack'],
  },
  {
    stepAlias: 'shipping',
    targetSteps: ['shipping'],
  },
  {
    stepAlias: 'tax',
    targetSteps: ['salesTax'],
  },
  {
    stepAlias: 'envelope',
    targetSteps: ['envelope', 'recipientAddress'],
  },
];

/**
 * Standalone envelopes have no card surfaces, so the `base_cost` row moves to the envelope group.
 *
 * Moving it is not optional. `usePriceCalculatorLineItemsToPriceSummaryLineItems` drops
 * `base_cost` from every group that does not carry `shouldIncludeQuantity`, so hiding the card
 * block without relocating the flag does not relocate the quantity row — it deletes it. The
 * summary then shows addressing and shipping under a total nothing explains, and nothing throws.
 *
 * `card` and `back` stay present with empty `targetSteps` so they resolve to `[]` rather than
 * `undefined`: consumers read `summary.back.length` directly.
 */
const ENVELOPE_LINE_ITEM_STEPS: LineItemStep[] = [
  {
    stepAlias: 'card',
    targetSteps: [],
  },
  {
    stepAlias: 'back',
    targetSteps: [],
  },
  {
    stepAlias: 'shipping',
    targetSteps: ['shipping'],
  },
  {
    stepAlias: 'tax',
    targetSteps: ['salesTax'],
  },
  {
    stepAlias: 'envelope',
    targetSteps: ['envelope', 'recipientAddress'],
    shouldIncludeQuantity: true,
  },
];

/**
 * Keyed on the product, deliberately not on the `printEnvelopeProduct` flag: an order already
 * placed must keep grouping correctly in the cart and in order history whatever the flag says
 * afterwards, and a rollback that flips it off must not reshape orders already taken.
 */
export const lineItemStepsForProduct = (productType: string | undefined): LineItemStep[] => (productType === 'envelope' ? ENVELOPE_LINE_ITEM_STEPS : CARD_LINE_ITEM_STEPS);

/**
 * Converts raw line items into grouped and translated line items.
 */
const useLineItemsByStep = (product: StationeryProduct, steps: LineItemStep[], items: StationeryPriceInfoRawLineItem[]): ValidLineItems => {
  const toLineItems = usePriceCalculatorLineItemsToPriceSummaryLineItems(product);

  const output = steps.reduce((acc, step) => {
    const { stepAlias, targetSteps, shouldIncludeQuantity } = step;
    const lineItemsForStep = toLineItems(items, targetSteps, shouldIncludeQuantity);
    return { ...acc, [stepAlias]: lineItemsForStep };
  }, {});

  return output as ValidLineItems;
};

export const cardPromotionToDiscount = (promotion: CardPromotion | undefined): StationeryDiscountInfo | undefined => {
  if (!promotion) {
    return undefined;
  }

  if (promotion.discountPercentage) {
    return {
      type: 'percent' as const,
      promoCode: promotion.code,
      percentage: promotion.discountPercentage,
    };
  }

  if (promotion.discountAmountInMinorUnits) {
    return {
      type: 'flatDiscountAmount' as const,
      promoCode: promotion.code,
      amount: promotion.discountAmountInMinorUnits,
    };
  }

  return undefined;
};

// New PriceSummaryController based on PrintPriceCalculator
// and new Product / Attribute / Option pricing structure
export const useProductPriceSummaryController = (
  priceCalculator: PrintPriceCalculator,
  cardConfig: ThemeJson,
  product: StationeryProduct,
  options: { attributeId: string; optionId: string }[],
  includeShipping: boolean,
  salesTax: Maybe<number>,
  promotion?: CardPromotion,
): PriceSummaryData => {
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  const deliveryInfo = useGetCardDeliveryInfo(cardConfig);

  const { orderCurrencyISO4217: orderCurrency } = useStationeryProductPricingContext(false);

  const discount = cardPromotionToDiscount(promotion);
  const quantity = cardConfig.customizations.quantity;
  const discountedPrice = priceCalculator.getProductPrice(product.id, quantity, options, includeShipping ? deliveryInfo?.deliveryMethodId : undefined, discount, salesTax);

  // We decided line items do no show the discounted price (because customers don't see a strikethough, they think it's normal price). The discount is only shown in the total.
  const nonDiscountedPrice = priceCalculator.getProductPrice(product.id, quantity, options, includeShipping ? deliveryInfo?.deliveryMethodId : undefined, undefined, salesTax);

  const currencyOptions = orderCurrency ? { ...PriceSummaryCurrencyFormatOptions, currency: orderCurrency } : PriceSummaryCurrencyFormatOptions;

  return {
    ...useLineItemsByStep(product, lineItemStepsForProduct(product.productType), nonDiscountedPrice.lineItems),
    total: minorUnitsToPriceString(discountedPrice.discounts?.preDiscountPriceInMinorUnits ?? discountedPrice.totalPriceInMinorUnits, currencyOptions),
    totalInMinorUnits: discountedPrice.unitPriceInMinorUnits, // not the total, actually unit price
    pricePerCard: minorUnitsToPriceString(discountedPrice.unitPriceInMinorUnits, currencyOptions),
    discountedTotal: discountedPrice.discounts ? minorUnitsToPriceString(discountedPrice.totalPriceInMinorUnits, currencyOptions) : null,
    discountPercentage: discount?.type === 'percent' ? discount.percentage : null,
  };
};
